import React, { useEffect, useState, useMemo, useRef } from "react";
import Map from "./Map";
import API from "../services/ApiService";
import CameraIcon from "./CameraIcon";
import JSConfetti from "js-confetti";
import Ranking from "./Ranking";
import { safeApiCall } from "../utils/safeApiCall";
import { toast } from "react-toastify";
import { useAuth } from "../context/AuthContext";
import { CiCircleInfo } from "react-icons/ci";

export default function GuessRoom(){
    const [selectedMap, setSelectedMap] = React.useState("Willowstreet.js")
    const [level, setLevel] = React.useState(0)
    const [levelBoundaries, setLevelBoundaries] = React.useState({min: 0, max: 0})
    const [dailyRoomImg, setDailyRoomImg] = React.useState()
    const [imgSize, setImgSize] = useState(null)
    const [focus, setFocus] = useState(null)
    const [attempt, setAttempt] = useState(0)
    const [answers, setAnswers] = useState([]);
    const [justLoading, setJustLoading] = useState(true);
    const finished = answers.some( room => room.is_correct );
    const newRowRef = React.useRef(null);
    const confettiRef = useRef(null);
    const { isLoggedIn, contextUser } = useAuth();

    const zoomLevels = [5, 4.7, 4.4, 4.1, 3.8, 3.5, 3.2, 2.9, 2.6, 2.2, 1.9, 1.6, 1.3, 1];

    if (!confettiRef.current) {
        confettiRef.current = new JSConfetti();
    }


    const onLevelDownClick = () => {
        if (level === levelBoundaries.min) return;
        setLevel( (prev) => prev - 1 )
    }

    const onLevelUpClick = () => {
        if (level === levelBoundaries.max) return;
        setLevel( (prev) => prev + 1 )
    }

    const handleSelectMap = (e) => {
        setSelectedMap(e.target.value)
        setLevel(0)
    }

    useEffect(() => {
        if (!contextUser) return;
        fetchDailyRoom();
    }, [contextUser])

    useEffect(() => {
        if (!contextUser) return;
        fetchAnswers();
    }, [isLoggedIn, contextUser]);


    const fetchDailyRoom = async () => {
        const res = await safeApiCall(API.getDailyRoom, {data: {image: undefined, focus: {x: undefined, y: undefined}}}, (err) => toast.error("Error fetching daily room"));
        setDailyRoomImg(res.data.image);
        setFocus({ x: res.data.focus.x, y: res.data.focus.y });
}
    const fetchAnswers = async () => {
        const res = await safeApiCall(API.getRoomAnswers, {data: []}, (err) => toast.error("Error fetching your answers"));
        setAnswers(res.data);
        setAttempt(res.data.length);
    }

    useEffect(() => {
            if (!newRowRef.current) return;
            const el = newRowRef.current;
            setTimeout(() => {
                el.classList.add("show");
            }, 50); 
        }, [answers]);

    useMemo(() => {
        if (finished && newRowRef.current != null) {
            confettiRef.current.addConfetti({
                confettiNumber: 10,
                emojis: ['👻', '🎉', '💀'],
            });
        }
    }, [answers]);

    useEffect(() => {
        if (finished){
            setAttempt(17)
        }
    }, [finished])

    function renderAnswers() {
        let placeNumber = 0;
        return (
        <ul className="place-answers-list" id="answers-list">
                <li id="place-answers-first-row"><p>Room</p><p>Map</p><p>Level</p></li>
                {answers.slice().reverse().map(room => {
                    const name = room.room.split(" ")
                    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                    .join(" ");
                    if (placeNumber == 0) {
                        placeNumber++;
                        return (
                            <li key={room.id} className={`place-answer-row ${justLoading ? 'show' : ''}`}
                            ref={(el) => {
                                if (justLoading) {
                                    return null;
                                } else {
                                    return newRowRef.current = el;
                                }
                            }}>
                                <span className={`answer-${room.is_correct}`}>{name}</span>
                                <span className={`answer-${room.map}`}>{room.map_name}</span>
                                <div className={`answer-${room.level} hunt-threshold`}>
                                    <span>{room.level == "higher" ? "▲" : "\u00A0"}</span>
                                    <span>{room.room_level}</span>
                                    <span>{room.level == "lower" ? "▼" : "\u00A0"}</span>
                                </div>
                            </li>
                        )
                    }
                    return (           
                    <li key={room.id}>
                        <span className={`answer-${room.is_correct}`}>{name}</span>
                        <span className={`answer-${room.map}`}>{room.map_name}</span>
                        <div className={`answer-${room.level} hunt-threshold`}>
                            <span>{room.level == "higher" ? "▲" : "\u00A0"}</span>
                            <span>{room.room_level}</span>
                            <span>{room.level == "lower" ? "▼" : "\u00A0"}</span>
                        </div>
                    </li>
                    );
                })}
            </ul>
        )
    }

    const scale = zoomLevels[attempt] || 1;

    return(
        <div className="guess-room">
            <div className="left-sidebar"></div>
            <div className="main-content">
                <div className="guess-room-description">
                    <div className="gamemode-description-title">
                        <CameraIcon classes="gamemode-description-icon"/>
                        <h1>Room Guesser</h1>
                    </div>
                    <p>Try to guess the daily room! Use the picture to recognize it. Each attempt will enlarge the picture and give you some clues about the map.</p>
                    {dailyRoomImg && (
                        <div className="daily-room-container">
                            <div className="daily-room-img-container">
                                <img
                                src={dailyRoomImg}
                                alt="Daily room"
                                className="daily-room-img"
                                onLoad={(e) => {
                                    setImgSize({
                                    width: e.target.naturalWidth,
                                    height: e.target.naturalHeight,
                                    });
                                }}
                                style={{
                                    transform: `scale(${scale})`,
                                    transformOrigin: `${focus.x * 100}% ${focus.y * 100}%`,
                                }}
                                />
                            </div>
                        </div>
                    )}
                    {finished && (
                        <h2>Congratulations! You guessed today's room!</h2>
                    )}
                </div>
                {!finished && (
                    <div className="map-container">
                        <select className="map-selector"
                            value={selectedMap}
                            onChange={(e) => handleSelectMap(e)}
                        >
                            <option value="Willowstreet.js">13 Willow Street</option>
                            <option value="Ridgeview.js">10 Ridgeview Court</option>
                            <option value="Edgefield.js">42 Edgefield Road</option>
                            <option value="TanglewoodOld.js">(Old)6 Tanglewood Drive</option>
                            <option value="MapleLodge.js">Maple Lodge Campsite</option>
                            <option value="CampWoodwind.js">Camp Woodwind</option>
                            <option value="NellsDinner.js">Nell's Dinner</option>
                            <option value="Grafton.js">Grafton Farmhouse</option>
                            <option value="Bleasdale.js">Bleasdale Farmhouse</option>
                            <option value="PointHope.js">Point Hope</option>
                            <option value="Prison.js">Prison</option>
                        </select>
                        <div className="map-selector-down-arrow">⌄</div>
                        <CiCircleInfo className="map-selector-info" title="More maps are coming soon!" color="#e0e0e060"/>
                        <div className="level-buttons-container">
                            <button onClick={onLevelUpClick}>▲</button>
                            <button onClick={onLevelDownClick}>▼</button>
                        </div>
                        <Map selectedMap={selectedMap} level={level} setLevelBoundaries={setLevelBoundaries} setAttempt={setAttempt} setAnswers={setAnswers} answers={answers} setJustLoading={setJustLoading}/>
                    </div>
                )}
                {answers.length > 0 && renderAnswers()}
            </div>
            <div className="right-sidebar">
                <Ranking rankingService={API.getRoomScores} streakService={API.getRoomStreak} color="neon-violet" finished={finished} userUpdate={newRowRef}/>
            </div>
        </div>
    )
}